import useApi from '@propertypal/shared/src/hooks/useApi';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { Agent } from '@propertypal/shared/src/types/agents';
import { ValuationResult } from '@propertypal/shared/src/types/valuations';
import getPostcodeImage from '@propertypal/shared/src/utils/getPostcodeImage';
import puralise from '@propertypal/shared/src/utils/puralise';
import { Heading, SubHeading, Text } from '@propertypal/web-ui/src/typography';
import AgentsBox from '@propertypal/web-ui/src/valuations/AgentsBox';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useEffect, useState } from 'react';
import ScaleLoader from 'react-spinners/ScaleLoader';
import { useTheme } from 'styled-components';
import EstateAgentsSelect from './EstateAgentsSelect';
import { Container, Content, FormBox, BgBox } from './InstantValuationBox.style';
import InstantValuationForm from './InstantValuationForm';
import InstantValuationResult from './InstantValuationResult';
import RequestValuationForm from './RequestValuationForm';

interface Props {
  baseUrl: string;
  title: string;
  description: string;
  mode: 'agent' | 'instant';
}

const VALID_STEPS = ['address', 'result', 'request', 'sent'];

const InstantValuationBox: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const [agentsSelected, setAgentsSelected] = useState<Agent[]>([]);
  const router = useRouter();
  const step = router.query.step && router.query.step.length > 0 ? router.query.step[0] : 'address';
  const resultId = router.query.step && router.query.step.length > 1 ? router.query.step[1] : '';
  const address = router.query.address && !Array.isArray(router.query.address) ? router.query.address : '';
  const loqId = router.query.loqateId && !Array.isArray(router.query.loqateId) ? router.query.loqateId : '';
  const [result, error, loading, fetchResult, setResult] = useApi<ValuationResult>('', false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [postcode, setPostcode] = useState(
    router.query.postcode && !Array.isArray(router.query.postcode) ? router.query.postcode : '',
  );
  const { largeBackground, smallBackground } = getPostcodeImage(props.mode, result?.avmRequest?.postcode || postcode);

  const updateStep = (nextStep: string) => {
    router.push(`${props.baseUrl}/${nextStep}`, undefined, {
      shallow: true,
    });
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });

    if (step === 'address' && !!result) {
      setResult(undefined);
    }

    if (
      !VALID_STEPS.includes(step) ||
      error ||
      (step !== 'address' && !resultId) ||
      (step === 'sent' && !agentsSelected.length)
    ) {
      updateStep('');
    } else if (step !== 'address' && !result) {
      fetchResult(`/valuation/${resultId}`);
    }
  }, [step, error, showRequestForm]);

  return (
    <Container>
      <BgBox largeBackground={largeBackground} smallBackground={smallBackground} />

      <Content>
        {step === 'address' && (
          <FormBox>
            <Heading>{props.title}</Heading>

            <Text color={theme.primary} fontSize={18} mt={15}>
              {props.description}
            </Text>

            <InstantValuationForm
              mode={props.mode}
              onPostcodeUpdate={setPostcode}
              onResult={(nextStep, mResult) => {
                setResult(mResult);
                updateStep(nextStep);
              }}
              address={address}
              loqId={loqId}
              pc={postcode}
            />
          </FormBox>
        )}

        {loading && (
          <FormBox>
            <ScaleLoader color={theme.backgroundMid} style={{ display: 'block', width: 40, margin: '30px auto' }} />
          </FormBox>
        )}

        {!loading && (
          <>
            {step === 'result' && result && (
              <FormBox>
                <InstantValuationResult
                  result={result}
                  onContactClick={() => {
                    trackGaEvent('valuation_contact_click', {});
                    updateStep(`request/${resultId}`);
                  }}
                  fetchResult={fetchResult}
                />
              </FormBox>
            )}

            {step === 'request' && result && (
              <>
                <FormBox show={!showRequestForm}>
                  <EstateAgentsSelect
                    mode={props.mode}
                    result={result}
                    onAgentsSelect={setAgentsSelected}
                    onRequestValuation={() => {
                      setShowRequestForm(true);
                    }}
                  />
                </FormBox>

                <FormBox show={showRequestForm}>
                  <RequestValuationForm
                    mode={props.mode}
                    agentsSelected={agentsSelected}
                    result={result}
                    onRequestSent={() => updateStep(`sent/${resultId}`)}
                    onBackClick={() => setShowRequestForm(false)}
                  />
                </FormBox>
              </>
            )}

            {step === 'sent' && (
              <FormBox>
                <SubHeading mb={15}>Thank you</SubHeading>

                <Text>
                  Your valuation request has been sent to the following {agentsSelected.length}{' '}
                  {puralise('agent', 'agents', agentsSelected.length)} who will be in touch to arrange a visit to the
                  property.
                </Text>

                <AgentsBox
                  agents={agentsSelected}
                  align="left"
                  backgroundColor={theme.backgroundLighter}
                  containerStyle={{ marginTop: 15 }}
                />
              </FormBox>
            )}
          </>
        )}
      </Content>
    </Container>
  );
};

export default InstantValuationBox;
